import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import clsx from "clsx";
// import * as moment from "moment";
import moment from "moment";
// Material
// import Typography from "@material-ui/core/Typography";
// import TextField from "@material-ui/core/TextField";
// Custom Hooks
import useTimeServices from "../../../CustomHooks/useTimeServices";
import useScheduledSlots from "../../../CustomHooks/useScheduledSlots";
// Styles
import useStyles from "./DeliveryTime.style.js";
// Constans
import {
  // DELIVERY_PICKUP_TYPES,
  ORDER_TYPES,
} from "../../../utils/Constants";
import DateSlotPicker from "../DateSlotPicker/CheckoutDateSlotPicker";
import TimePicker from "../TimePicker/CheckoutTimePicker";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import { OutlinedInput, FormControl, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import CheckBox from "../../Controls/Checkbox/CheckBox";
// import { showSnackbarAction } from "../../../store/Snackbar/SnackbarAction";
// import { convertToMoment } from "../../../utils/TimeUtils";
import { generateDateSlots } from "../../../utils/utils";
import Btn from "../../Controls/Button/Button";
import Modal from "../../Material/Modal/Modal";
import TableListing from "../../TableListing/TableListing";
import SpecialAlert from "../../SpecialAlert/SpecialAlert";
import {
  clearTableSelection,
  clearTipAmount,
  saveCheckoutForm,
} from "../../../store/Cart/CartActions";
import { ENABLE_TABLE_PRE_BOOKING_AND_ORDER } from "../../../config/app-config";
import useCheckoutForm from "../../../CustomHooks/useCheckoutForm.js";
import { resetWallet } from "../../../store/Profile/ProfileActions.js";
import useWalletPoints from "../../../CustomHooks/useWalletPoints.js";
import useDynamicHotelData from "../../../CustomHooks/useDynamicHotelData";

const DeliveryTime = ({
  value,
  orderType,
  onSelect = () => {},
  onOrderTypeSelect = () => {},
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  
  // Basic state
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [dateSlots, setDateSlots] = useState([]);
  const [orderTimeType, setOrderTimeType] = useState("ASAP");
  const { formValues, setFormValues } = useCheckoutForm();

  // Refs
  const isInitialRender = useRef(true);

  // Auto-save function to Redux
  const autoSaveToRedux = useCallback((deliveryType, deliveryTime) => {
    if (!orderType) return;
    
    
    // Calculate deliveryDate from deliveryTime
    let deliveryDate = null;
    if (deliveryTime) {
      try {
        deliveryDate = DateTime.fromISO(deliveryTime).toFormat("yyyy-MM-dd");
      } catch (error) {
        deliveryDate = DateTime.now().toFormat("yyyy-MM-dd");
      }
    } else if (deliveryType === "ASAP") {
      deliveryDate = DateTime.now().toFormat("yyyy-MM-dd");
    }
    
    // Save to Redux
    const reduxData = {
      orderType: orderType,
      deliveryType: deliveryType,
      deliveryTime: deliveryTime,
      deliveryDate: deliveryDate
    };
    
    dispatch(saveCheckoutForm(reduxData));
  }, [orderType, dispatch]);

  // Hooks
  const { isAsapAvailable } = useTimeServices();
  const { 
    getAvailableDates, 
    getTimeSlots, 
    isScheduledAvailable: scheduledAvailableFromHook, 
    getBufferTime
  } = useScheduledSlots();

  const {
    isMultipleOrderType,
    getOrderTypes,
  } = useDynamicHotelData();
  
  const { hotel } = useSelector((state) => state.hotel);
  const { selectedTable, checkoutDetails } = useSelector((state) => state.cart);
  
  // Additional state
  const [dateSlotsError, setDateSlotsError] = useState(false);
  const [showModal, toggleModal] = useState(false);
  const [isCheckBoxDisabled, setIsCheckBoxDisabled] = useState(false);
  const [showOrderTypeAlert, setShowOrderTypeAlert] = useState(false);
  const [blockedOrderType, setBlockedOrderType] = useState(null);
  const { resetWalletProducts } = useWalletPoints();

  // Initialize state from value prop
  useEffect(() => {
    if (value && value.deliveryType && value.deliveryTime) {
      // Set order time type from value prop
      setOrderTimeType(value.deliveryType);
      
      if (value.deliveryType === "SCHEDULED" && value.deliveryTime) {
        try {
          // Parse the deliveryTime to set date and time
          const deliveryMoment = moment(value.deliveryTime);
          const dateStr = deliveryMoment.format("LL"); // "September 4, 2025"
          
          setDate(dateStr);
          setTime(value.deliveryTime);
        } catch (error) {
        }
      }
    }
  }, [value]);

  // Determine ASAP label based on order type
  const asapLabel = useMemo(() => {
    if (!orderType) return { available: false, label: "ASAP : Not Available" };
    
    if (orderType === ORDER_TYPES.DELIVERY) {
      return isAsapAvailable(ORDER_TYPES.DELIVERY)
        ? { available: true, label: `ASAP: in ${getBufferTime(ORDER_TYPES.DELIVERY)}-${getBufferTime(ORDER_TYPES.DELIVERY) + 5} mins` }
        : { available: false, label: "ASAP : Not Available" };
    }
    if (orderType === ORDER_TYPES.PICKUP) {
      return isAsapAvailable(ORDER_TYPES.PICKUP)
        ? { available: true, label: `ASAP: in ${getBufferTime(ORDER_TYPES.PICKUP)}-${getBufferTime(ORDER_TYPES.PICKUP) + 5} mins` }
        : { available: false, label: "ASAP : Not Available" };
    }
    return { available: false, label: "ASAP : Not Available" };
  }, [orderType, isAsapAvailable, getBufferTime]);

  // Check if scheduled is available
  const scheduledAvailable = useMemo(() => {
    if (!scheduledAvailableFromHook || !orderType) {
      return false;
    }
    
    const dates = getAvailableDates;
    if (!dates || dates.length === 0) {
      return false;
    }
    
    // Check if any date has valid time slots
    for (const date of dates) {
      const timeSlots = getTimeSlots(date.date, orderType);
      if (timeSlots && timeSlots.length > 0) {
        return true;
      }
    }
    
    return false;
  }, [scheduledAvailableFromHook, orderType, getTimeSlots, getAvailableDates]);

  // Generate date slots
  const generateDatePickerSlots = useCallback(() => {
    if (!hotel || !orderType) return;
    
    let orderTypeValue = orderType;
    if (orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      orderTypeValue = ORDER_TYPES.OPENING;
    }
    
    const allDates = generateDateSlots(hotel, orderTypeValue) || [];
    
    // Filter out dates that don't have any time slots
    const validDates = allDates.filter(dateSlot => {
      const timeSlots = getTimeSlots(dateSlot.date, orderTypeValue);
      return timeSlots && timeSlots.length > 0;
    });
    
    setDateSlots(validDates);
    setDateSlotsError(!validDates || validDates.length === 0);
  }, [hotel, orderType, getTimeSlots]);

  // Get time slots for a specific date
  const getTimeSlotsForDate = useCallback((selectedDate) => {
    if (!hotel || !selectedDate || !orderType) {
      return [];
    }

    let orderTypeValue = orderType;
    if (orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      orderTypeValue = ORDER_TYPES.OPENING;
    }

    return getTimeSlots(selectedDate, orderTypeValue);
  }, [hotel, orderType, getTimeSlots]);

  // Check scheduled availability for a specific order type (without switching state)
  const isScheduledAvailableForOrderType = useCallback((targetOrderType) => {
    if (!hotel || !targetOrderType) return false;

    let orderTypeValue = targetOrderType;
    if (targetOrderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      orderTypeValue = ORDER_TYPES.OPENING;
    }

    const allDates = generateDateSlots(hotel, orderTypeValue) || [];
    if (!allDates.length) return false;

    for (const dateSlot of allDates) {
      const timeSlots = getTimeSlots(dateSlot.date, orderTypeValue);
      if (timeSlots && timeSlots.length > 0) {
        return true;
      }
    }
    return false;
  }, [hotel, getTimeSlots]);

  // Initialize order time type
  useEffect(() => {
    if (!orderType) return;
    
    // Use existing Redux values if available
    if (checkoutDetails?.deliveryType) {
      if (checkoutDetails.deliveryType === "ASAP" && asapLabel.available) {
        setOrderTimeType("ASAP");
      } else if (checkoutDetails.deliveryType === "SCHEDULED" && scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
      } else {
        // Fallback to first available
        if (asapLabel.available) {
          setOrderTimeType("ASAP");
        } else if (scheduledAvailable) {
          setOrderTimeType("SCHEDULED");
        }
      }
    } else {
      // Default logic
      if (asapLabel.available) {
        setOrderTimeType("ASAP");
      } else if (scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
      }
    }
  }, [orderType, asapLabel.available, scheduledAvailable, checkoutDetails?.deliveryType]);

  // Generate date slots when component mounts
  useEffect(() => {
    if (hotel && orderType) {
      generateDatePickerSlots();
    }
  }, [hotel, orderType, generateDatePickerSlots]);

  // Set default date and time for SCHEDULED
  useEffect(() => {
    if (orderTimeType !== "SCHEDULED" || !orderType || dateSlots.length === 0) {
      return;
    }

    // If current selection is valid for the selected date, keep it
    const isCurrentSelectionValid = (() => {
      if (!date || !time) return false;
      const slots = getTimeSlotsForDate(date);
      return Array.isArray(slots) && slots.some((s) => s.value === time);
    })();
    if (isCurrentSelectionValid) {
      return;
    }

    // If Redux has a previously selected scheduled time and it's valid for current order type, use it
    const existingScheduledISO =
      checkoutDetails?.deliveryType === "SCHEDULED" ? checkoutDetails?.deliveryTime : null;
    if (existingScheduledISO) {
      try {
        // Match date format to dateSlots (LL)
        const existingDateStr = moment(existingScheduledISO).format("LL");
        const slots = getTimeSlotsForDate(existingDateStr);
        const exists = Array.isArray(slots) && slots.some((s) => s.value === existingScheduledISO);
        if (exists) {
          if (date !== existingDateStr) setDate(existingDateStr);
          if (time !== existingScheduledISO) setTime(existingScheduledISO);
          return; // keep Redux as is
        }
      } catch (e) {
        // ignore parsing errors and fallback to first available
      }
    }

    // Fallback to first available date and first time slot
    const firstDateSlot = dateSlots[0];
    const timeSlots = getTimeSlotsForDate(firstDateSlot.date);
    if (timeSlots && timeSlots.length > 0) {
      if (date !== firstDateSlot.date) setDate(firstDateSlot.date);
      if (time !== timeSlots[0].value) setTime(timeSlots[0].value);
      autoSaveToRedux("SCHEDULED", timeSlots[0].value);
    }
  }, [orderTimeType, orderType, dateSlots, date, time, getTimeSlotsForDate, autoSaveToRedux, checkoutDetails?.deliveryType, checkoutDetails?.deliveryTime]);

  // Handle order type changes
  useEffect(() => {
    if (!orderType) return;
    
    
    // Clear existing date/time when order type changes
    setDate(null);
    setTime(null);
    
    // Check if order type has changed and clear previous Redux state
    if (checkoutDetails?.orderType && checkoutDetails.orderType !== orderType) {
      // Clear previous delivery details when switching order types
      dispatch(saveCheckoutForm({
        orderType: orderType,
        deliveryType: null,
        deliveryTime: null,
        deliveryDate: null
      }));
    }
    
    // Respect user's previous delivery type selection only if it's for the same order type
    if (checkoutDetails?.deliveryType && checkoutDetails?.orderType === orderType) {
      if (checkoutDetails.deliveryType === "ASAP" && asapLabel.available) {
        setOrderTimeType("ASAP");
        // Calculate ASAP time
        const currentTime = new Date();
        const bufferTime = getBufferTime(orderType);
        const asapTime = new Date(currentTime.getTime() + (bufferTime * 60 * 1000));
        autoSaveToRedux("ASAP", asapTime.toISOString());
        
      } else if (checkoutDetails.deliveryType === "SCHEDULED" && scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
        // Date/time will be set by the other useEffect
      }
    } else {
      // Default logic for new order type
      if (asapLabel.available) {
        setOrderTimeType("ASAP");
        const currentTime = new Date();
        const bufferTime = getBufferTime(orderType);
        const asapTime = new Date(currentTime.getTime() + (bufferTime * 60 * 1000));
        autoSaveToRedux("ASAP", asapTime.toISOString());
        
      } else if (scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
      }
    }
  }, [orderType, asapLabel.available, scheduledAvailable, checkoutDetails?.deliveryType, checkoutDetails?.orderType, getBufferTime, autoSaveToRedux, dispatch]);

  // Additional cleanup when order type changes
  useEffect(() => {
    if (!orderType) return;
    
    
    // If order type has changed and we have previous delivery details, ensure they're cleared
    if (checkoutDetails?.orderType && checkoutDetails.orderType !== orderType) {
      // Force clear any remaining delivery details
      setDate(null);
      setTime(null);
      
      // Clear only delivery-related fields
      dispatch(saveCheckoutForm({
        orderType: orderType,
        deliveryType: null,
        deliveryTime: null,
        deliveryDate: null
      }));
      
      // Set appropriate default delivery type for the new order type
      if (asapLabel.available) {
        setOrderTimeType("ASAP");
        const currentTime = new Date();
        const bufferTime = getBufferTime(orderType);
        const asapTime = new Date(currentTime.getTime() + (bufferTime * 60 * 1000));
        autoSaveToRedux("ASAP", asapTime.toISOString());
      } else if (scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
      }
    }
  }, [orderType, checkoutDetails?.orderType, asapLabel.available, scheduledAvailable, getBufferTime, autoSaveToRedux]);

  // Set default delivery type when order type changes
  useEffect(() => {
    if (!orderType) return;
    
    
    // Only set default if we don't already have a valid delivery type for this order type
    if (!checkoutDetails?.deliveryType || checkoutDetails?.orderType !== orderType) {
      if (asapLabel.available) {
        setOrderTimeType("ASAP");
        const currentTime = new Date();
        const bufferTime = getBufferTime(orderType);
        const asapTime = new Date(currentTime.getTime() + (bufferTime * 60 * 1000));
        autoSaveToRedux("ASAP", asapTime.toISOString());
      } else if (scheduledAvailable) {
        setOrderTimeType("SCHEDULED");
      }
    }
  }, [orderType, asapLabel.available, scheduledAvailable, checkoutDetails?.deliveryType, checkoutDetails?.orderType, getBufferTime, autoSaveToRedux]);

  // Handle date and time selection
  const onSelectDataAndTime = useCallback(({ value, typeOfOrder } = {}, type) => {
    if (!value) return;
    
    if (type === "date") {
      setDate(value);
      
      // Since all dates in dateSlots now have time slots, we can directly get time slots
      const timeSlots = getTimeSlotsForDate(value);
      if (timeSlots && timeSlots.length > 0) {
        setTime(timeSlots[0].value);
        autoSaveToRedux("SCHEDULED", timeSlots[0].value);
      }
      
    } else if (type === "time") {
      setTime(value);
      autoSaveToRedux("SCHEDULED", value);
    }
  }, [getTimeSlotsForDate, autoSaveToRedux]);

  // Handle radio change
  const handleRadioChange = useCallback((event) => {
    const newOrderTimeType = event.target.value;
    setOrderTimeType(newOrderTimeType);
    
    // Clear existing date/time when switching delivery types
    if (newOrderTimeType === "ASAP") {
      setDate(null);
      setTime(null);
      
      if (orderType) {
        const currentTime = new Date();
        const bufferTime = getBufferTime(orderType);
        const asapTime = new Date(currentTime.getTime() + (bufferTime * 60 * 1000));
        const asapTimeISO = asapTime.toISOString();
        
        onSelect("ASAP", asapTimeISO);
        autoSaveToRedux("ASAP", asapTimeISO);
      }
      
    } else if (newOrderTimeType === "SCHEDULED") {
      // Clear ASAP time from Redux when switching to SCHEDULED
      dispatch(saveCheckoutForm({
        orderType: orderType,
        deliveryType: "SCHEDULED",
        deliveryTime: null,
        deliveryDate: null
      }));
      // Date/time will be set by useEffect
    }
  }, [orderType, getBufferTime, onSelect, autoSaveToRedux, dispatch]);

  // Handle order type change
  const handleOrderTypeChange = useCallback((newOrderType) => {
    if (!newOrderType || newOrderType === orderType) return;


    // Check availability for the target order type without committing the change
    const asapOk = isAsapAvailable(newOrderType);
    const scheduledOk = isScheduledAvailableForOrderType(newOrderType);

    if (!asapOk && !scheduledOk) {
      // Block switch, show alert, and keep previous order type
      setBlockedOrderType(newOrderType);
      setShowOrderTypeAlert(true);
      return;
    }

    // Clear existing date/time state
    setDate(null);
    setTime(null);

    // Clear Redux state for the previous order type before switching
    
    // Clear only delivery-related fields to avoid interfering with other form data
    dispatch(saveCheckoutForm({
      orderType: newOrderType,
      deliveryType: null,
      deliveryTime: null,
      deliveryDate: null
    }));

    // Don't call onOrderTypeSelect to avoid interference from parent component
    // Instead, handle the order type change entirely within this component
    
    // Reset wallet and other related states
    dispatch(resetWallet());
    resetWalletProducts(3);
    dispatch(clearTipAmount());
    

  }, [orderType, dispatch, resetWalletProducts, isAsapAvailable, isScheduledAvailableForOrderType, checkoutDetails]);

  // Check if order type is disabled
  useEffect(() => {
    if (hotel) {
      if (hotel?.is_preorder === 0) {
        if (
          hotel?.deliveryAvailable === false ||
          hotel?.pickupAvailable === false
        ) {
          setIsCheckBoxDisabled(true);
        } else {
          setIsCheckBoxDisabled(false);
        }
      } else {
        setIsCheckBoxDisabled(false);
      }
    }
  }, [hotel]);

  // Clear table selection when order type changes
  useEffect(() => {
    if (!orderType) return;
    if (orderType !== ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      dispatch(clearTableSelection());
    }
  }, [orderType, dispatch]);

  // Helper function to get order type display name
  const getOrderTypeDisplayName = useCallback((orderType) => {
    switch (orderType) {
      case ORDER_TYPES.DELIVERY:
        return "Delivery";
      case ORDER_TYPES.PICKUP:
        return "Pickup";
      case ORDER_TYPES.TABLE_BOOKING_AND_ORDERING:
        return "Table Booking & Ordering";
      case ORDER_TYPES.DINEIN:
        return "Dine-in";
      default:
        return orderType;
    }
  }, []);

  // Show loading if no date slots
  // if (dateSlots?.length === 0) {
  //   return (
  //     <div className={clsx(classes.root)}>
  //       <div className={clsx(classes.container)}>
  //         <p>
  //           {!dateSlotsError
  //             ? "Loading time slots...."
  //             : "Delivery time finished"}
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div id="checkout-deliveryPickupTypeForm" className={clsx(classes.root)}>
      <div>
        <table className={`w-100 ${classes.borderTable}`}>
          <tbody>
            <tr>
              <td colSpan="2">
                <div className="row m-0">
                  {getOrderTypes?.map((o, i) => (
                    <div key={i} className={`col ${classes.checkBoxTd}`}>
                      <CheckBox
                        isFromCheckout={true}
                        text={o.title}
                        checked={formValues?.orderType === o.type}
                        disabled={
                          (o.type === "DELIVERY" &&
                            hotel?.is_preorder === 0 &&
                            !hotel?.deliveryAvailable) ||
                          (o.type === "PICKUP" &&
                            hotel?.is_preorder === 0 &&
                            !hotel?.pickupAvailable)
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          handleOrderTypeChange(o.type);
                        }}
                        classes={{
                          checkBoxContainer: classes.checkBoxContainer,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </td>
            </tr>
            {ENABLE_TABLE_PRE_BOOKING_AND_ORDER && (
              <tr>
                <td
                  colSpan={
                    orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING ? 1 : 2
                  }
                >
                  <div
                    className={`${classes.checkBoxTd} ${
                      orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING
                        ? "border-0"
                        : ""
                    }`}
                  >
                    <CheckBox
                      text={"Table booking"}
                      checked={
                        orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING
                      }
                      isFromCheckout={true}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleOrderTypeChange(ORDER_TYPES.TABLE_BOOKING_AND_ORDERING);
                      }}
                      classes={{
                        checkBoxContainer: classes.checkBoxContainer,
                      }}
                    />
                  </div>
                </td>
                {orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING && (
                  <td>
                    <div
                      className={`${classes.selectTableCont} justify-conent-center align-items-center`}
                    >
                      {!selectedTable?.id ? (
                        <Btn
                          className={`${classes.btn}`}
                          text="Choose Table"
                          onClick={() => {
                            toggleModal(true);
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <OutlinedInput
                            fullWidth
                            classes={{
                              input: classes.input,
                            }}
                            value={selectedTable.name}
                            disabled={true}
                          />
                          <Btn
                            handleClick={() => {
                              dispatch(clearTableSelection());
                            }}
                            text="Clear"
                            className={classes.loginBtn}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            )}
            {/* ASAP/SCHEDULED Radio Buttons */}
            <tr>
              <td colSpan="2">
                <div style={{ marginBottom: "16px",paddingLeft: "10px" }}>
                  <FormControl>
                    <RadioGroup
                      value={orderTimeType}
                      onChange={handleRadioChange}
                    >
                      <FormControlLabel
                        value="ASAP"
                        control={<Radio />}
                        label={asapLabel.label}
                        disabled={!asapLabel.available}
                      />
                      <FormControlLabel
                        value="SCHEDULED"
                        control={<Radio />}
                        label="Schedule for later"
                        disabled={!scheduledAvailable}
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
              </td>
            </tr>
            {/* Date and Time Pickers - Only show for SCHEDULED */}
            {orderTimeType === "SCHEDULED" && (
              <tr>
                <td className="w-50">
                  <DateSlotPicker
                    slots={dateSlots}
                    className={`w-100 ${classes.dateTimeSelection}`}
                    onChange={onSelectDataAndTime}
                    onClick={() => {}} // Simplified
                    value={date ? { value: date } : null}
                    setDate={setDate}
                  />
                </td>
                <td className="w-50">
                  <TimePicker
                    slots={getTimeSlotsForDate(date || new Date().toISOString().split('T')[0])}
                    className={`w-100 ${classes.dateTimeSelection}`}
                    onChange={onSelectDataAndTime}
                    onClick={() => {}} // Simplified
                    value={time ? { value: time } : null}
                    setTime={setTime}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Modal
        customView={true}
        open={showModal}
        handleClose={() => toggleModal(false)}
      >
        <TableListing
          handleClose={() => {
            toggleModal(false);
          }}
        />
      </Modal>
      
      {/* Alert for blocked order type switching */}
      <SpecialAlert
        open={showOrderTypeAlert}
        title={`${getOrderTypeDisplayName(blockedOrderType)} is currently unavailable. This order type has no ASAP and scheduled slots available at the moment. We've kept your current order type active.`}
        handleClose={() => setShowOrderTypeAlert(false)}
        isBtnRight={true}
      />
    </div>
  );
};

export default DeliveryTime;